// ─────────────────────────────────────────────────────────────────────────────
// rhythmRatio.js — Pure helpers for the Rhythm Ratio (RHY) metric.
//
// Rhythm Ratio measures the relationship between drive-phase duration and
// recovery-phase duration within each stroke cycle. Expressed as 1:X.X.
//
//   RHY = recoveryMs / driveMs        →   displayed as "1:" + ratio.toFixed(1)
//
// Coaching zones (per product spec):
//   1.0 – 1.4   severely rushed
//   1.5 – 1.9   rushed
//   2.0 – 2.4   standard (acceptable at race rate)
//   2.5 – 3.0   excellent — elite steady state
//   3.0+        relaxed paddle
// ─────────────────────────────────────────────────────────────────────────────

/** Numeric ratio. Returns null when inputs are unusable (zero / negative / NaN). */
export function rhythmRatio(driveMs, recoveryMs) {
  const d = Number(driveMs);
  const r = Number(recoveryMs);
  if (!Number.isFinite(d) || !Number.isFinite(r)) return null;
  if (d <= 0 || r <= 0) return null;
  const ratio = r / d;
  // Clamp to a sane physical band (0.5 – 10) so detection glitches don't
  // produce wild values that scroll the metric tile.
  if (ratio < 0.5 || ratio > 10) return null;
  return ratio;
}

/** "1:2.3" formatted string. Returns "—" for null inputs so the tile renders cleanly. */
export function formatRhythmRatio(ratio) {
  if (ratio == null || !Number.isFinite(ratio)) return '—';
  return `1:${ratio.toFixed(1)}`;
}

/** Coaching zone label for a given ratio. Returns null when no ratio. */
export function rhythmZone(ratio) {
  if (ratio == null || !Number.isFinite(ratio)) return null;
  if (ratio < 1.5) return 'severely-rushed';
  if (ratio < 2.0) return 'rushed';
  if (ratio < 2.5) return 'standard';
  if (ratio < 3.0) return 'excellent';
  return 'relaxed';
}

/**
 * Tailwind text-colour class for a given zone (or a raw ratio). Designed for the
 * Coach Dashboard 8-lane view + the inline metric tile colour-pill.
 *
 *   severely-rushed → rose   (immediate technique correction)
 *   rushed          → amber  (slow the slide)
 *   standard        → sky    (acceptable at race pace)
 *   excellent       → emerald (target zone)
 *   relaxed         → zinc   (normal at UT3 paddle)
 */
export function rhythmZoneColor(zoneOrRatio) {
  const z = typeof zoneOrRatio === 'string' ? zoneOrRatio : rhythmZone(zoneOrRatio);
  switch (z) {
    case 'severely-rushed': return 'text-rose-600 dark:text-rose-400';
    case 'rushed':          return 'text-amber-600 dark:text-amber-400';
    case 'standard':        return 'text-sky-600 dark:text-sky-400';
    case 'excellent':       return 'text-emerald-600 dark:text-emerald-400';
    case 'relaxed':         return 'text-zinc-600 dark:text-zinc-400';
    default:                return '';
  }
}

/**
 * Rolling average over the last N strokes. Takes an array of `{drive, recovery}`
 * objects (milliseconds) and returns a stable ratio that ignores any glitchy
 * samples. Used by the IMU pipeline to smooth out per-stroke jitter.
 */
export function smoothRhythmRatio(strokes, window = 3) {
  if (!Array.isArray(strokes) || strokes.length === 0) return null;
  const slice = strokes.slice(-window);
  let drive = 0;
  let recovery = 0;
  let count = 0;
  for (const s of slice) {
    const r = rhythmRatio(s.drive, s.recovery);
    if (r != null) {
      drive += s.drive;
      recovery += s.recovery;
      count++;
    }
  }
  if (count === 0) return null;
  return rhythmRatio(drive / count, recovery / count);
}
